/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-case-declarations */
import React, { useState, useEffect } from 'react'

import { CheckboxChangeEvent, ColorPickerChangeEvent } from '@progress/kendo-react-inputs'
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns'

import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { selectedStyleSettings } from 'store/workspace/selectors';
import {selectSelectedTab } from 'store/workspace/slice';
import { T_changeStyleSettings, T_deleteStyleSettings, T_getStyleSettings } from 'store/workspace/thunk';
import { rgbToHex } from 'helpers/functions/commons';
import { getMapViewFilteredPointsThunk } from 'store/mapView/thunk';
import { getPlotThunk } from 'store/explore/thunk';

// Define a type for style settings
interface StyleSettings {
    color: string;
    font: { name: string; family: string };
    shape: { id: string; url: string };
    sizeFont: { name: string; value: string };
    sizeShape: "x1" | "x2" | "x3" | "x4" | "x5";
    position: { label: string; value: string };
    border: boolean;
    transparent?: number;
}

export const useStylesSettings = (pointData: any, closeStyles: any, handleCloseInfoWindow: any, onSave?: ()=>void) => {
    const dispatch = useAppDispatch()
    const styleSettings = useAppSelector(selectedStyleSettings)
    const selectedTab = useAppSelector(selectSelectedTab)
    const workspaceId = useAppSelector(store => store.workspaceSlice.workspaceId)
    const plotData = useAppSelector(store => store.exploreSlice.plot)
    const [color, setColor] = useState(styleSettings.color)
    const [font, setFont] = useState(styleSettings.font)
    const [shape, setShape] = useState(styleSettings.shape)
    const [sizeFont, setSizeFont] = useState(styleSettings.sizeFont)
    const [sizeShape, setSizeShape] = useState(styleSettings.sizeShape)
    const [position, setPosition] = useState(styleSettings.position)
    const [border, setBorder] = useState(styleSettings.border)
    const [transparent, setTransparent] = useState(0.9)
    const [isLoading, setisLoading] = useState(false)
    const [userDataDrop, setUserDataDrop] = useState({label: 'Default', value: 'default'});
    const [tempData, setTempData] = useState<Partial<StyleSettings>>({});
    const benchmarkPlot = useAppSelector((store) => store?.exploreSlice?.benchmark?.benchmark?.generatedPlot)
    const getPointIdentifier = (data: any) => {
        return data?.id || 
               data?.object?.identifierFieldId || 
               data?.object?.IdentifierFieldId || 
               data?.object?.from?.identifierFieldId || 
               data?.object?.to?.identifierFieldId || 
               data?.identifierFieldId;
    };
    
    const isUserData = (data: any) => {
        return data?.object?.isUserData || data?.data?.isUserData;
    };
    
    useEffect(() => {
        const fetchData = async () => {
            setisLoading(true);
            
            const point = getPointIdentifier(pointData);
            const type = isUserData(pointData) ? 2 : 1;
            
            await dispatch(T_getStyleSettings({ 
                workspaceId, 
                identifierFieldId: point, 
                optionType: type, 
                plotType: selectedTab?.type ? +selectedTab?.type || undefined : undefined 
            }));
            
            setisLoading(false);
        };
        fetchData();
    }, [workspaceId, pointData, dispatch, selectedTab]);

    useEffect(() => {
        setColor(styleSettings?.color?.includes('rgb') ? rgbToHex(styleSettings?.color) : styleSettings?.color);
        setFont(styleSettings.font);
        setShape(styleSettings.shape);
        setSizeFont(styleSettings.sizeFont);
        setSizeShape(styleSettings.sizeShape);
        setPosition(styleSettings.position);
        setBorder(styleSettings.border);
        const alpha = findAlpha(styleSettings?.color);
        setTransparent(alpha === 0 ? 0.99 : alpha);
    }, [styleSettings]);

    // Extract and apply settings from benchmark plot data if available
    useEffect(() => {
        if (!benchmarkPlot?.userData || !pointData) return;
        
        const pointId = getPointIdentifier(pointData);
        if (!pointId) return;
        
        // Find the point in benchmark userData
        let foundPointOptions = null;
        let foundMarker = null;
        
        for (const userDataItem of benchmarkPlot.userData) {
            if (userDataItem.ids && Array.isArray(userDataItem.ids)) {
                const index = userDataItem.ids.findIndex((id: string) => id === pointId);
                if (index !== -1) {
                    foundPointOptions = userDataItem.crossPlotPointOptions?.[index];
                    foundMarker = userDataItem.markers?.[index];
                    break;
                }
            }
        }
        
        if (!foundPointOptions) return;
        
        // Map the numeric values to the proper objects
        const shapeMap: Record<number, { id: string, url: string }> = {
            1: { id: "circle", url: "circle" },
            2: { id: "square", url: "square" },
            3: { id: "diamond", url: "diamond" },
            4: { id: "triangle-up", url: "triangle-up" },
            5: { id: "triangle-down", url: "triangle-down" }
        };
        
        const sizeMap: Record<number, "x1" | "x2" | "x3" | "x4" | "x5"> = {
            1: "x1",
            2: "x2",
            3: "x3",
            4: "x4",
            5: "x5"
        };
        
        const positionMap: Record<number, { label: string, value: string }> = {
            1: { label: "Top", value: "top" },
            2: { label: "Bottom", value: "bottom" },
            3: { label: "Left", value: "left" },
            4: { label: "Right", value: "right" }
        };
        
        // Extract and set color from stringColor
        if (foundPointOptions.stringColor) {
            const hex = foundPointOptions.stringColor.startsWith('#') 
                ? foundPointOptions.stringColor.substring(1) 
                : foundPointOptions.stringColor;
            setColor('#' + hex);
        } else if (foundMarker?.color) {
            setColor(foundMarker.color);
        }
        
        // Set shape
        if (foundPointOptions.shapeType !== undefined) {
            const mappedShape = shapeMap[foundPointOptions.shapeType] || shapeMap[1];
            setShape(mappedShape);
        }
        
        // Set font
        if (foundPointOptions.font) {
            setFont({
                name: foundPointOptions.font,
                family: foundPointOptions.font
            });
        }
        
        // Set font size
        if (foundPointOptions.fontSizeType !== undefined) {
            setSizeFont({
                name: foundPointOptions.fontSizeType.toString(),
                value: foundPointOptions.fontSizeType.toString()
            });
        }
        
        // Set marker size
        if (foundPointOptions.markerSizeType !== undefined) {
            const mappedSize = sizeMap[foundPointOptions.markerSizeType] || "x3";
            setSizeShape(mappedSize);
        }
        
        // Set position
        if (foundPointOptions.positionType !== undefined) {
            const mappedPosition = positionMap[foundPointOptions.positionType] || positionMap[1];
            setPosition(mappedPosition);
        }
        
        // Set border
        if (foundPointOptions.hasBorder !== undefined) {
            setBorder(foundPointOptions.hasBorder);
        }
        
    }, [benchmarkPlot?.userData, pointData]);

    useEffect(() => {
        if (styleSettings.color) {
            let red = 0, green = 0, blue = 0;
            
            if (styleSettings.color.includes('rgba')) {
                const valuesString = styleSettings.color.replace("rgba(", "").replace(")", "");
                const rgbArray = valuesString.split(",");
                
                const parseColorValue = (value: string): number => {
                    const parsed = parseInt(value.trim(), 10);
                    return isNaN(parsed) ? 0 : parsed;
                };
                
                red = parseColorValue(rgbArray[0]);
                green = parseColorValue(rgbArray[1]);
                blue = parseColorValue(rgbArray[2]);
            }
            else if (styleSettings.color.includes('rgb')) {
                const valuesString = styleSettings.color.replace("rgb(", "").replace(")", "");
                const rgbArray = valuesString.split(",");
                
                const parseColorValue = (value: string): number => {
                    const parsed = parseInt(value.trim(), 10);
                    return isNaN(parsed) ? 0 : parsed;
                };
                
                red = parseColorValue(rgbArray[0]);
                green = parseColorValue(rgbArray[1]);
                blue = parseColorValue(rgbArray[2]);
            }
            else if (styleSettings.color.startsWith('#')) {
                const hex = styleSettings.color.substring(1);
                red = parseInt(hex.substring(0, 2), 16);
                green = parseInt(hex.substring(2, 4), 16);
                blue = parseInt(hex.substring(4, 6), 16);
            }
            const alpha = transparent === undefined || transparent == 1 ? 0.9 : transparent;
            const colorRgb = `rgba(${red},${green},${blue},${alpha})`;
            
            setTempData({
                color: colorRgb,
                font: styleSettings?.font,
                shape: styleSettings?.shape,
                sizeFont: styleSettings?.sizeFont,
                sizeShape: styleSettings?.sizeShape,
                position: styleSettings?.position,
                border: styleSettings?.border
            });

        }
    }, [styleSettings, transparent]);
    
    const findAlpha = (value: string | null): number => {
        if (!value) return 0.99; // default value, e.g., fully opaque
        
        // Handle rgba format
        const rgbaRegex = /rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*([\d.]+)\s*\)/;
        const rgbaMatch = value.match(rgbaRegex);
        if (rgbaMatch) {
            return parseFloat(rgbaMatch[1]);
        }
        
        // Handle rgb format - if it's rgb, return fully opaque
        const rgbRegex = /rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)/;
        if (rgbRegex.test(value)) {
            return 0.99;
        }
        
        // Handle hex format - if it's hex, return fully opaque
        const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
        if (hexRegex.test(value)) {
            return 0.99;
        }
        
        return 0.99; // Default to fully opaque for any other format
    }

    const handleColorChange = (e: ColorPickerChangeEvent) => {
        setColor(e.value)
    }

    const handleColChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setColor(e.target.value)

    }

    const handleTransparentChange = (e: number) => {
        setTransparent(e)
    }



    const handleFontChange = (event: DropDownListChangeEvent) => {
        setFont(event.target.value);
    };

    const handleShapeChange = (event: DropDownListChangeEvent) => {
        setShape(event.target.value);
    };

    const handleFontSizeChange = (event: DropDownListChangeEvent) => {
        setSizeFont(event.target.value);
    };


    const handleShapeSizeChange = (event: DropDownListChangeEvent) => {
        setSizeShape(event.target.value);
    };


    const handlePositionChange = (event: DropDownListChangeEvent) => {
        setPosition(event.target.value);
    };

    const handleBorderChange = (e: CheckboxChangeEvent) => {
        setBorder(e.value)
    }

    const handleReset = async () => {
        try {
            if (!styleSettings?.optionId) {
                console.warn('No option ID available to reset styles');
                return;
            }

            // Close dialogs first
            handleCloseInfoWindow?.();
            closeStyles?.();
            
            // Delete style settings
            await dispatch(T_deleteStyleSettings({ 
                workspaceId, 
                optionId: styleSettings.optionId 
            }));
            
            // Get point identifier and type
            const point = getPointIdentifier(pointData);
            const type = isUserData(pointData) ? 2 : 1;
            
            // Refresh style settings
            await dispatch(T_getStyleSettings({ 
                workspaceId, 
                identifierFieldId: point, 
                optionType: type 
            }));
            
            // Call onSave if provided
            if (onSave) {
                await onSave();
            }
            
            // Refresh map view
            await dispatch(getMapViewFilteredPointsThunk({ workspaceId }));
        } catch (error) {
            console.error('Error resetting style settings:', error);
        }
    }

    const handleUserDataDrop = (event: DropDownListChangeEvent) => {
        setUserDataDrop(event.target.value);
    };
    
    const updateAllUserDataPoints = async (plotData: any[], type: number, styleSettings: Partial<StyleSettings>) => {
        // Collect user data points from both plotData and benchmarkPlot
        const crossPlotUserDataPoints = plotData.filter(item => 
            item && item.isUserData && (item.id || item.identifierFieldId || (item.ids && item.ids[0]))
        );
        
        // Collect benchmark plot user data points
        const benchmarkUserDataPoints: any[] = [];
        if (benchmarkPlot?.userData && Array.isArray(benchmarkPlot.userData)) {
            benchmarkPlot.userData.forEach((userDataItem: any) => {
                if (userDataItem.ids && Array.isArray(userDataItem.ids)) {
                    userDataItem.ids.forEach((id: string, index: number) => {
                        if (id) {
                            benchmarkUserDataPoints.push({
                                id: id,
                                crossPlotPointOptions: userDataItem.crossPlotPointOptions?.[index],
                                markers: userDataItem.markers?.[index],
                                isBenchmarkData: true,
                                index: index
                            });
                        }
                    });
                }
            });
        }
        
        // Combine all user data points
        const allUserDataPoints = [...crossPlotUserDataPoints, ...benchmarkUserDataPoints]
        
        for (const item of allUserDataPoints) {
            const itemId = item.id || item.identifierFieldId || (item.ids && item.ids[0]);
            
            if (!itemId) continue;
            
            // Extract existing style information directly from the plot data item
            const existingSettings: Partial<StyleSettings> = {};
            
            // Handle benchmark plot data with crossPlotPointOptions (array element)
            if (item.isBenchmarkData && item.crossPlotPointOptions) {
                const opt = item.crossPlotPointOptions;
                
                // Convert stringColor (hex) to rgba
                if (opt.stringColor) {
                    const hex = opt.stringColor.startsWith('#') ? opt.stringColor.substring(1) : opt.stringColor;
                    const red = parseInt(hex.substring(0, 2), 16);
                    const green = parseInt(hex.substring(2, 4), 16);
                    const blue = parseInt(hex.substring(4, 6), 16);
                    existingSettings.color = `rgba(${red},${green},${blue},0.99)`;
                }
                
                // Map other properties
                if (opt.font) {
                    existingSettings.font = {
                        name: opt.font,
                        family: opt.font
                    };
                }
                
                if (opt.shapeType !== undefined) {

                    const shapeMap: Record<number, { id: string, url: string }> = {
                        1: { id: "Circle", url: `${window.location.origin}/circle.png` },
                        2: { id: "Square", url: `${window.location.origin}/square.png` },
                        3: { id: "Triangle", url: `${window.location.origin}/triangle-up.png` },
                    };
                    existingSettings.shape = shapeMap[opt.shapeType];
                    
                }
                
                if (opt.fontSizeType !== undefined) {
                    existingSettings.sizeFont = {
                        name: opt.fontSizeType.toString(),
                        value: opt.fontSizeType.toString()
                    };
                }
                
                if (opt.markerSizeType !== undefined) {
                    const sizeMap: Record<number, "x1" | "x2" | "x3" | "x4" | "x5"> = {
                        1: "x1",
                        2: "x2",
                        3: "x3",
                        4: "x4",
                        5: "x5"
                    };
                    existingSettings.sizeShape = sizeMap[opt.markerSizeType] || "x3";
                }
                
                if (opt.positionType !== undefined) {
                    const positionMap: Record<number, { label: string, value: string }> = {
                        1: { label: "Top", value: "top" },
                        2: { label: "Bottom", value: "bottom" },
                        3: { label: "Left", value: "left" },
                        4: { label: "Right", value: "right" }
                    };
                    existingSettings.position = positionMap[opt.positionType] || positionMap[1];
                }
                
                if (opt.hasBorder !== undefined) {
                    existingSettings.border = opt.hasBorder;
                }
                
                // Handle markers array for benchmark data
                if (item.markers && item.markers.color) {
                    if (!existingSettings.color) {
                        existingSettings.color = item.markers.color;
                    }
                }
            }
            // Handle crossplot data with crossPlotPointOption (single object)
            else if (item.crossPlotPointOption) {
                const opt = item.crossPlotPointOption;
                
                // Convert color array to rgba string
                if (opt.color && Array.isArray(opt.color) && opt.color.length >= 4) {
                    existingSettings.color = `rgba(${opt.color[0]},${opt.color[1]},${opt.color[2]},${opt.color[3]})`;
                }
                
                // Map other properties
                if (opt.font) {
                    existingSettings.font = {
                        name: opt.font,
                        family: opt.font
                    };
                }
                
                if (opt.shapeType !== undefined) {
                    const shapeMap: Record<number, { id: string, url: string }> = {
                        1: { id: "circle", url: "circle" },
                        2: { id: "square", url: "square" },
                        3: { id: "diamond", url: "diamond" },
                        4: { id: "triangle-up", url: "triangle-up" },
                        5: { id: "triangle-down", url: "triangle-down" }
                    };
                    existingSettings.shape = shapeMap[opt.shapeType] || shapeMap[1];
                }
                
                if (opt.fontSizeType !== undefined) {
                    existingSettings.sizeFont = {
                        name: opt.fontSizeType.toString(),
                        value: opt.fontSizeType.toString()
                    };
                }
                
                if (opt.markerSizeType !== undefined) {
                    const sizeMap: Record<number, "x1" | "x2" | "x3" | "x4" | "x5"> = {
                        1: "x1",
                        2: "x2",
                        3: "x3",
                        4: "x4",
                        5: "x5"
                    };
                    existingSettings.sizeShape = sizeMap[opt.markerSizeType] || "x3";
                }
                
                if (opt.positionType !== undefined) {
                    const positionMap: Record<number, { label: string, value: string }> = {
                        1: { label: "Top", value: "top" },
                        2: { label: "Bottom", value: "bottom" },
                        3: { label: "Left", value: "left" },
                        4: { label: "Right", value: "right" }
                    };
                    existingSettings.position = positionMap[opt.positionType] || positionMap[1];
                }
                
                if (opt.hasBorder !== undefined) {
                    existingSettings.border = opt.hasBorder;
                }
            }
            
            // If marker properties exist, they can provide fallbacks
            if (item.marker && !item.isBenchmarkData) {
                // Only set if not already set from crossPlotPointOption
                if (!existingSettings.color && item.marker.color) {
                    existingSettings.color = item.marker.color;
                }
                
                // Shape/symbol
                if (!existingSettings.shape && item.marker.symbol) {
                    existingSettings.shape = {
                        id: item.marker.symbol,
                        url: item.marker.symbol
                    };
                }
                
                // Size
                if (!existingSettings.sizeShape && item.marker.size) {
                    const size = parseInt(item.marker.size);
                    const sizeMap: Record<number, "x1" | "x2" | "x3" | "x4" | "x5"> = {
                        5: "x1",
                        7: "x2",
                        10: "x3",
                        12: "x4",
                        15: "x5"
                    };
                    existingSettings.sizeShape = sizeMap[size] || "x3";
                }
            }
            
            // Extract font information from textfont
            if (item.textfont && !existingSettings.font) {
                existingSettings.font = {
                    name: item.textfont.family || "Arial",
                    family: item.textfont.family || "Arial"
                };
                
                if (item.textfont.size && !existingSettings.sizeFont) {
                    existingSettings.sizeFont = {
                        name: item.textfont.size.toString(),
                        value: item.textfont.size.toString()
                    };
                }
            }
            
            const mergedSettings = {
                ...existingSettings,
                ...styleSettings
            };
            const filteredSettings: Partial<StyleSettings> = {};
            
            Object.entries(mergedSettings).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    // Type assertion to safely set the property
                    (filteredSettings as any)[key] = value;
                }
            });
            await dispatch(T_changeStyleSettings({
                workspaceId,
                optionType: type,
                identifierFieldId: itemId,
                styleSettings: filteredSettings
            }));
        }
    };

    const handleSave = async () => {
        try {
            // Convert hex color to rgba
            const red = parseInt(color.substring(1, 3), 16);
            const green = parseInt(color.substring(3, 5), 16);
            const blue = parseInt(color.substring(5, 7), 16);
            // Ensure we never save with zero transparency (which would make dots invisible)
            const alpha = transparent === undefined || transparent == 1 ? 0.99 : transparent;
            const colorRgb = `rgba(${red},${green},${blue},${alpha})`;
            
            const point = getPointIdentifier(pointData);
            const type = isUserData(pointData) ? 2 : 1;
            
            // Close modals first
            handleCloseInfoWindow?.();
            closeStyles?.();
            
            // Base style settings
            const baseStyleSettings = {
                color: colorRgb,
                font,
                shape,
                sizeFont,
                sizeShape,
                position,
                border
            };
            
            // If not user data, just update the specific point
            if (type !== 2) {
                await dispatch(T_changeStyleSettings({
                    workspaceId,
                    optionType: type,
                    identifierFieldId: point,
                    styleSettings: baseStyleSettings
                }));
            } 
            // Handle user data based on selected option
            else {
                // Create a property map that only includes the exact property to update
                const propertyMap: Record<string, Partial<typeof baseStyleSettings>> = {
                    'all': baseStyleSettings,
                    'allColour': colorRgb ? { color: colorRgb } : {},
                    'allShape': shape ? { shape } : {},
                    'allPointSize': sizeShape ? { sizeShape } : {},
                    'allBorder': { border }, // Boolean is always defined
                    'allFont': font ? { font } : {},
                    'allLabelSize': sizeFont ? { sizeFont } : {},
                    'allPoints': position ? { position } : {}
                };
                
                // Default case or selected property
                const selectedStyleSettings = propertyMap[userDataDrop.value] || baseStyleSettings;
                
                // If "default" is selected, only update the specific point
                if (userDataDrop.value === 'default') {
                    await dispatch(T_changeStyleSettings({
                        workspaceId,
                        optionType: type,
                        identifierFieldId: point,
                        styleSettings: baseStyleSettings
                    }));
                } else {
                    // Otherwise update all user data points
                    await updateAllUserDataPoints(plotData, type, selectedStyleSettings);
                }
            }
            
            // Refresh data
            await dispatch(T_getStyleSettings({ 
                workspaceId, 
                identifierFieldId: point, 
                optionType: type 
            }));
            onSave?.();
            
            // Update plot and map if needed
            if (selectedTab?.id) {
                await dispatch(getPlotThunk({ plotId: selectedTab?.id, workspaceId }));
            }
            
            await dispatch(getMapViewFilteredPointsThunk({ workspaceId }));
        } catch (error) {
            console.error("Error saving style settings:", error);
        }
    };

    return {
        color,
        handleColorChange,
        font,
        handleFontChange,
        shape,
        handleShapeChange,
        sizeFont,
        handleFontSizeChange,
        sizeShape,
        handleShapeSizeChange,
        position,
        handlePositionChange,
        border,
        handleBorderChange,
        handleReset,
        handleSave,
        handleColChange,
        transparent,
        handleTransparentChange,
        isLoading,
        userDataDrop,
        handleUserDataDrop,
    }

}